import type { TFunction } from "react-i18next";
import type { TwoLineOption } from "../../../components/SelectBoxV2/TwoLineOptionLabel";
import { filterOptionBySearchText } from "../../../components/SelectBoxV2/TwoLineOptionLabel";
import type {
  IAddress,
  OptionType,
  PointOfContact,
  Tenant,
  User,
  UUID,
} from "../../../types/types";
import { getCountryOption } from "../../../util/location";
import { formatFullAddress } from "../../../util/util";

export const NEW_ADDRESS_VALUE = "new_address";

const SEPARATOR = " • ";

const isFilled = (value: string | null | undefined): value is string =>
  !!value && value.trim() !== "";

const joinFilled = (
  parts: (string | null | undefined)[],
  separator = SEPARATOR
) => parts.filter(isFilled).join(separator);

/**
 * `value` is the buyer's user ID, so that two buyers of the same company are
 * distinct options. Transactions still store the company, which is
 * `tenant_id`.
 */
export type BuyerOption = TwoLineOption<UUID> & {
  tenant_id: UUID;
  email_address: string;
  company_name: string | null;
};

export const buyerUserToOption = (user: User): BuyerOption => {
  const primaryText = joinFilled([
    joinFilled([user.firstname, user.lastname], " "),
    user.email_address,
  ]);
  const companyName = isFilled(user.company_name) ? user.company_name : null;
  return {
    value: user.id,
    tenant_id: user.tenant_id,
    email_address: user.email_address,
    company_name: companyName,
    label: joinFilled([primaryText, companyName]),
    primaryText,
    supportingText: companyName ?? undefined,
  };
};

export const shippingAddressToOption = (
  address: IAddress,
  countries: OptionType<string>[],
  t: TFunction
): TwoLineOption<string> => {
  const primaryText = formatFullAddress(address);
  const locationId = address.external_identity?.external_id;
  const supportingText = joinFilled([
    isFilled(locationId) ? `${t("Location ID")}: ${locationId}` : null,
    isFilled(address.crm_id) ? `${t("CRM ID")}: ${address.crm_id}` : null,
    isFilled(address.tax_id) ? `${t("Tax ID")}: ${address.tax_id}` : null,
  ]);
  return {
    value: address.id,
    label: joinFilled([primaryText, supportingText]),
    primaryText,
    supportingText: supportingText || undefined,
    searchText: joinFilled(
      [
        primaryText,
        address.address1,
        address.address2,
        address.city,
        address.state,
        address.state_display_name,
        address.subdivision_name,
        address.country,
        getCountryOption(countries, address.country)?.label,
        address.postal_code,
        address.tax_id,
        address.crm_id,
        locationId,
      ],
      " "
    ),
  };
};

/**
 * True while an existing location that has points of contact is selected and
 * none of them has been chosen yet. "New Address" and locations without POCs
 * never need a choice.
 */
export const isShippingPointOfContactMissing = ({
  buyer,
  shippingAddressOption,
  pointOfContactOption,
}: {
  buyer: Tenant | null;
  shippingAddressOption: OptionType<string> | null | undefined;
  pointOfContactOption: OptionType<string> | null | undefined;
}) => {
  if (pointOfContactOption?.value) {
    return false;
  }
  const address = buyer?.addresses?.find(
    (a) => a.id === shippingAddressOption?.value
  );
  return (address?.points_of_contact?.length ?? 0) > 0;
};

type FilterableOption = { label: string; data: TwoLineOption<unknown> };

export const filterShippingAddressOption = (
  option: FilterableOption,
  rawInput: string
) =>
  option.data.value === NEW_ADDRESS_VALUE ||
  filterOptionBySearchText(option, rawInput);

export type PointOfContactOption = TwoLineOption<string> & {
  phoneDigits: string;
};

export const shippingPointOfContactToOption = (
  poc: PointOfContact
): PointOfContactOption => {
  const name =
    joinFilled([poc.contact_first_name, poc.contact_last_name], " ") ||
    poc.contact_name;
  const countryCode = isFilled(poc.country) ? `(${poc.country})` : null;
  const primaryText = joinFilled([
    name,
    joinFilled([poc.business_unit, countryCode], " "),
  ]);
  const supportingText = joinFilled([poc.email_address, poc.phone_number]);
  return {
    value: poc.id,
    label: joinFilled([primaryText, supportingText]),
    primaryText,
    supportingText: supportingText || undefined,
    searchText: joinFilled([name, poc.email_address, poc.phone_number], " "),
    phoneDigits: (poc.phone_number ?? "").replace(/\D/g, ""),
  };
};

export const filterPointOfContactOption = (
  option: { label: string; data: PointOfContactOption },
  rawInput: string
) => {
  if (filterOptionBySearchText(option, rawInput)) {
    return true;
  }
  // Phone numbers are compared digits-only so that "+61 (400) 123" finds
  // "+61400123" and the other way round.
  const inputDigits = rawInput.replace(/\D/g, "");
  return (
    inputDigits !== "" &&
    !/[a-z]/i.test(rawInput) &&
    option.data.phoneDigits.includes(inputDigits)
  );
};
